import axios from 'axios'
import { getToken, getTerminal, removeToken } from '@/store/local/auth'
import authService from '@/services/AuthService'
import { Message } from 'element-ui'

// 防重复处理 token 失效的标志位
let isHandlingTokenExpired = false

// 当前是否在「带 token 的面试链接」入口页（该页其他接口报错不跳转登录，仅 link/open 返回 token 无效时才跳转）
function isLinkEntryProfilePage() {
  if (typeof window === 'undefined' || !window.location) return false
  const path = (window.location.pathname || '') + (window.location.hash || '') + (window.location.search || '')
  return path.includes('dashboard/profile') && path.includes('token=')
}

// 当前是否在 token 面试进行页；该页应沿用 link/open 建立的链接态，避免被普通登录态校验误伤
function isTokenInterviewPage() {
  if (typeof window === 'undefined' || !window.location) return false
  const path = window.location.pathname || ''
  return path.includes('/store/interview_ing_by_b')
}

function shouldSkipLoginExpiredRedirect() {
  return isLinkEntryProfilePage() || isTokenInterviewPage()
}

function getRouteTokenFallback() {
  if (typeof window === 'undefined' || !window.location) return ''
  try {
    const searchParams = new URLSearchParams(window.location.search || '')
    const directToken = searchParams.get('token')
    if (directToken) return directToken
    const payloadRaw = searchParams.get('payload')
    if (!payloadRaw) return ''
    const decoded = decodeURIComponent(payloadRaw)
    const payload = JSON.parse(decoded)
    return payload && payload.token ? String(payload.token) : ''
  } catch (e) {
    return ''
  }
}

function shouldOmitTerminalHeader() {
  if (typeof window === 'undefined' || !window.location) return false
  const path = window.location.pathname || ''
  return path.includes('/store/interview_ing_webview')
}

function shouldOmitInjectedHeaders() {
  return false
}

// 创建 axios 实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // API 基础路径
  withCredentials: true, // 跨域请求时发送 cookie
  timeout: 600000, // 请求超时时间
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    if (!config.headers) config.headers = {}
    const token = getToken() || getRouteTokenFallback()
    if (token) {
      config.headers['token'] = token
    }
    if (shouldOmitTerminalHeader()) {
      config.headers['terminal'] = '1'
    } else {
      config.headers['terminal'] = getTerminal() || '4'
    }
    return config
  },
  error => {
    console.log(error) // 打印错误信息用于调试
    return Promise.reject('失败'+error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response
    if (res.status != 200) {
      if (res.status == 403 || res.status == 401) {
        // token 链接入口页 / token 面试页不触发全局跳转登录，由业务流程自行处理
        if (!shouldSkipLoginExpiredRedirect() && !isHandlingTokenExpired) {
          isHandlingTokenExpired = true
          authService.handleLoginExpired('您的登录已过期，请重新登录')
          setTimeout(() => { isHandlingTokenExpired = false }, 2000)
        }
      }
      return Promise.reject(new Error(res.message || 'Error'))
    } else {
      // 全局处理 token 无效（防重复处理）；token 链接入口页 / token 面试页不跳转，由业务流程决定后续动作
      if (res.data && typeof res.data.msg === 'string' && res.data.msg.includes('token参数无效')) {
        if (!shouldSkipLoginExpiredRedirect() && !isHandlingTokenExpired) {
          isHandlingTokenExpired = true
          try { removeToken() } catch (e) {}
          authService.handleLoginExpired('您的登录已失效，请重新登录')
          setTimeout(() => { isHandlingTokenExpired = false }, 2000)
        }
        return Promise.reject(new Error(res.data.msg))
      }
      // 统一处理业务错误 - 登录相关接口
      if (res.data && res.data.code !== 1) {
        const url = response.config.url || '';
        // 判断是否为登录相关的接口
        if (url.includes('/api/login/') || url.includes('/api/sms/sendCode')) {
          // 登录相关错误，统一在拦截器处理
          const errorMessage = res.data.msg
          
          // 先创建错误对象
          const error = new Error(errorMessage);
          error.code = res.data.code;
          error.response = res.data;
          
          // 显示错误消息
          Message({
            message: errorMessage,
            type: 'error',
            duration: 3000
          });
          // 确保Promise被reject
          return Promise.reject(error);
        }
      }
      return res
    }
  },
  error => {
    console.log('err' + error) // 打印错误信息用于调试
    
    // 处理401/403错误（防重复处理）；token 链接入口页 / token 面试页不跳转登录
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      if (!shouldSkipLoginExpiredRedirect() && !isHandlingTokenExpired) {
        isHandlingTokenExpired = true
        authService.handleLoginExpired('您的登录已过期，请重新登录')
        setTimeout(() => { isHandlingTokenExpired = false }, 2000)
      }
    }
    
    return Promise.reject(error)
  }
)

export default service
